/**
 * CatastroDataService - Servicio de datos catastrales desde R2
 *
 * Accede a parcelas/inmuebles de municipios andaluces desde R2:
 * - Carga por municipio desde R2 Worker (catastro/{ine5}.json.gz)
 * - Búsqueda espacial por proximidad (Flatbush)
 * - Búsqueda fuzzy por nombre de vía (uFuzzy)
 * - Búsqueda por referencia catastral, polígono/parcela, dirección
 * - Lazy loading + caché LRU de municipios
 *
 * Datos: Catastro — Dirección General del Catastro (2.4M edificaciones)
 * CRS: EPSG:25830 (UTM30 ETRS89)
 *
 * @module services/data/CatastroDataService
 * @version 1.1.0
 * @date 2026-03-19
 * @changes
 *   1.1.0 — F1.07 v6.1: patrón 6 «Pol X - PYYYY» en detectCadastralPattern()
 */

import Flatbush from 'flatbush';
import uFuzzy from '@leeoniya/ufuzzy';
import type { CatastroR2Parcel, CatastroR2Result } from '../../types/catastroR2';
import { X_RANGE_ANDALUCIA, Y_RANGE_ANDALUCIA, tipologiaACodigoCatastro } from '../../types/catastroR2';
import { TipologiaPTEL, TIPOLOGIAS_VALUES } from '../../types/tipologia';
import { PROVINCIAS_ANDALUCIA } from '../../types/catastro';
import { WORKER_BASE } from '@/lib/config/workerEndpoints';

// =============================================================================
// TIPOS
// =============================================================================

/** Dataset catastral de un municipio con índices */
export interface CatastroDataset {
  codINE: string;
  municipio: string;
  parcels: CatastroR2Parcel[];
  count: number;
}

/** Municipio cargado con índices de búsqueda */
interface LoadedMunicipality {
  dataset: CatastroDataset;
  spatialIndex: Flatbush;
  fuzzyIndex: uFuzzy;
  fuzzyHaystack: string[];
  /** Mapa refcat → índice en array parcels */
  refcatMap: Map<string, number>;
  loadedAt: number;
}

/** Resultado de detección de patrón catastral en texto */
export interface CadastralPatternMatch {
  patternType: 'full_ref' | 'polygon_parcel' | 'sigpac';
  polygon?: string;
  parcel?: string;
  fullRef?: string;
  confidence: number;
}

/** Opciones de búsqueda espacial */
export interface CatastroSpatialOptions {
  maxDistance?: number;
  limit?: number;
  usos?: string[];
}

/** Estadísticas del servicio */
export interface CatastroServiceStats {
  municipiosLoaded: number;
  totalParcels: number;
  loadedCodINEs: string[];
}

// =============================================================================
// CONSTANTES
// =============================================================================

/**
 * URL base del Worker R2 (alias local).
 * Fuente única de verdad: `@/lib/config/workerEndpoints` (`WORKER_BASE`).
 */
const R2_BASE_URL = WORKER_BASE;
const R2_TIMEOUT = 15000;
const MAX_CACHED_MUNICIPALITIES = 10;

const UFUZZY_OPTIONS: uFuzzy.Options = {
  intraMode: 1,
  intraIns: 1,
  interIns: 6,
  intraSub: 1,
  intraTrn: 1,
  intraDel: 1,
};

// =============================================================================
// SERVICIO PRINCIPAL
// =============================================================================

export class CatastroDataService {
  private cache = new Map<string, LoadedMunicipality>();
  private accessOrder: string[] = [];

  // ---------------------------------------------------------------------------
  // CARGA DE DATOS
  // ---------------------------------------------------------------------------

  async loadMunicipality(codINE: string): Promise<CatastroDataset> {
    if (!/^\d{5}$/.test(codINE)) throw new Error(`Código INE inválido: ${codINE}`);
    const prov = codINE.substring(0, 2);
    if (!PROVINCIAS_ANDALUCIA.includes(prov as (typeof PROVINCIAS_ANDALUCIA)[number])) {
      throw new Error(`Provincia no andaluza: ${prov}`);
    }

    const cached = this.cache.get(codINE);
    if (cached) {
      this.updateAccessOrder(codINE);
      return cached.dataset;
    }

    const parcels = await this.fetchFromR2(codINE);
    const dataset: CatastroDataset = {
      codINE,
      municipio: parcels.length > 0 ? parcels[0].municipio : '',
      parcels,
      count: parcels.length,
    };

    const spatialIndex = this.buildSpatialIndex(parcels);
    const { fuzzyIndex, fuzzyHaystack } = this.buildFuzzyIndex(parcels);
    const refcatMap = this.buildRefcatMap(parcels);

    this.addToCache(codINE, { dataset, spatialIndex, fuzzyIndex, fuzzyHaystack, refcatMap, loadedAt: Date.now() });
    console.log(`[CatastroDataService] Cargado ${codINE} (${dataset.municipio}): ${dataset.count} parcelas`);
    return dataset;
  }

  private async fetchFromR2(codINE: string): Promise<CatastroR2Parcel[]> {
    const provincia = codINE.substring(0, 2);
    const url = `${R2_BASE_URL}/catastro/${provincia}/${codINE}.json.gz`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), R2_TIMEOUT);

    try {
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      // Guard: R2 puede devolver objeto wrapper o jsdom puede parsear diferente
      if (!Array.isArray(data)) {
        console.warn(`[CatastroDataService] Respuesta no-array para ${codINE}: ${typeof data}`);
        return [];
      }
      return data as CatastroR2Parcel[];
    } catch (error) {
      clearTimeout(timeoutId);
      console.error(`[CatastroDataService] Error cargando ${codINE}:`, error);
      throw new Error(`No se pudieron cargar datos Catastro para municipio ${codINE}`, { cause: error });
    }
  }

  // ---------------------------------------------------------------------------
  // CONSTRUCCIÓN DE ÍNDICES
  // ---------------------------------------------------------------------------

  private buildSpatialIndex(parcels: CatastroR2Parcel[]): Flatbush {
    // Guard defensivo: parcels no-array o vacío → índice dummy funcional
    if (!Array.isArray(parcels) || parcels.length === 0) {
      const empty = new Flatbush(1);
      empty.add(0, 0, 0, 0); // Dummy item para que finish() no falle
      empty.finish();
      return empty;
    }
    const index = new Flatbush(parcels.length);
    for (const p of parcels) index.add(p.x, p.y, p.x, p.y);
    index.finish();
    return index;
  }

  private buildFuzzyIndex(parcels: CatastroR2Parcel[]): { fuzzyIndex: uFuzzy; fuzzyHaystack: string[] } {
    const seen = new Set<string>();
    const fuzzyHaystack: string[] = [];
    for (const p of parcels) {
      const key = `${p.via} ${p.nombre_via}`.trim().toUpperCase();
      if (key && !seen.has(key)) {
        seen.add(key);
        fuzzyHaystack.push(key);
      }
    }
    return { fuzzyIndex: new uFuzzy(UFUZZY_OPTIONS), fuzzyHaystack };
  }

  private buildRefcatMap(parcels: CatastroR2Parcel[]): Map<string, number> {
    const map = new Map<string, number>();
    for (let i = 0; i < parcels.length; i++) {
      // RUSTICA-FIX S2: indexar por refcat_canonico (14 chars, line[30:44]) con
      // fallback a refcat legacy. Ningún caller pasa la clave legacy mutilada
      // (los refcats vienen de detectCadastralPattern sobre el ODT, en forma
      // canónica), así que un único índice canónico no rompe lookups urbanos.
      const rc = (parcels[i].refcat_canonico ?? parcels[i].refcat)?.replace(/\s/g, '').toUpperCase();
      if (rc) map.set(rc, i);
    }
    return map;
  }

  // ---------------------------------------------------------------------------
  // BÚSQUEDA POR REFERENCIA CATASTRAL
  // ---------------------------------------------------------------------------

  async searchByRefcat(codINE: string, refcat: string): Promise<CatastroR2Result | null> {
    await this.loadMunicipality(codINE);
    const cached = this.cache.get(codINE);
    if (!cached) return null;

    const { dataset, refcatMap } = cached;
    const norm = refcat.replace(/\s/g, '').toUpperCase();

    // Búsqueda exacta
    const exactIdx = refcatMap.get(norm);
    if (exactIdx !== undefined) {
      const p = dataset.parcels[exactIdx];
      if (this.isValidCoord(p)) return this.toResult(p, 95, 10);
    }

    // Búsqueda por prefijo (14 chars = finca)
    if (norm.length >= 14) {
      const prefix = norm.substring(0, 14);
      for (const [rc, idx] of refcatMap) {
        if (rc.startsWith(prefix)) {
          const p = dataset.parcels[idx];
          if (this.isValidCoord(p)) return this.toResult(p, 90, 10);
        }
      }
    }

    return null;
  }

  // ---------------------------------------------------------------------------
  // BÚSQUEDA POR POLÍGONO/PARCELA
  // ---------------------------------------------------------------------------

  async searchByPolygonParcel(codINE: string, polygon: string, parcel: string): Promise<CatastroR2Result[]> {
    await this.loadMunicipality(codINE);
    const cached = this.cache.get(codINE);
    if (!cached) return [];

    const polyPad = polygon.replace(/\D/g, '').padStart(3, '0');
    const parcelPad = parcel.replace(/\D/g, '').padStart(5, '0');

    return cached.dataset.parcels
      .filter(p => {
        // RUSTICA-FIX S2: el slice pol/parc se toma del refcat_canonico (14 chars)
        // con fallback a refcat legacy. Sin esto, la rústica era ilegible (la
        // clave legacy [23:37] no alinea polígono en substring(6,9)/(9,14)).
        const rc = (p.refcat_canonico ?? p.refcat)?.replace(/\s/g, '').toUpperCase() ?? '';
        return rc.length >= 14 && rc.substring(6, 9) === polyPad && rc.substring(9, 14) === parcelPad && this.isValidCoord(p);
      })
      .slice(0, 10)
      .map(p => this.toResult(p, 90, 10));
  }

  // ---------------------------------------------------------------------------
  // DETECCIÓN DE PATRONES CATASTRALES EN TEXTO
  // ---------------------------------------------------------------------------

  detectCadastralPattern(text: string): CadastralPatternMatch | null {
    if (!text) return null;
    const norm = text.toUpperCase().trim();

    // 1. Referencia catastral completa (14-20 chars)
    const fullRef = norm.match(/\b([0-9]{5}[A-Z][0-9]{3}[0-9A-Z]{5,11})\b/);
    if (fullRef) {
      return { patternType: 'full_ref', fullRef: fullRef[1], polygon: fullRef[1].substring(6, 9), parcel: fullRef[1].substring(9, 14), confidence: 95 };
    }

    // 2. SIGPAC: "18-051-0-0-8-398"
    const sigpac = norm.match(/(?:SIGPAC[:\s]*)?(\d{2})-(\d{3})-(\d)-(\d)-(\d{1,3})-(\d{1,5})/);
    if (sigpac) {
      return { patternType: 'sigpac', polygon: sigpac[5].padStart(3, '0'), parcel: sigpac[6].padStart(5, '0'), confidence: 90 };
    }

    // 3. "Pol. X P-Y" / "Pol X Parcela Y"
    const polP = norm.match(/POL[.\sÍI]*(?:GONO)?\s*(\d{1,3})\s*[,.\s]+P(?:ARC(?:ELA)?)?[\-.\s]*(\d{1,5})/);
    if (polP) {
      return { patternType: 'polygon_parcel', polygon: polP[1].padStart(3, '0'), parcel: polP[2].padStart(5, '0'), confidence: 85 };
    }

    // 4. "P-Y Pol-X" (inverso)
    const pPol = norm.match(/P(?:ARC(?:ELA)?)?[\-.\s]*(\d{1,5})\s+POL[.\sÍI]*(?:GONO)?\s*[\-.\s]*(\d{1,3})/);
    if (pPol) {
      return { patternType: 'polygon_parcel', polygon: pPol[2].padStart(3, '0'), parcel: pPol[1].padStart(5, '0'), confidence: 85 };
    }

    // 5. "Polígono X, Parcela Y" (extenso)
    const ext = norm.match(/POL[IÍ]GONO\s+(\d{1,3})[,;\s]+PARCELA\s+(\d{1,5})/);
    if (ext) {
      return { patternType: 'polygon_parcel', polygon: ext[1].padStart(3, '0'), parcel: ext[2].padStart(5, '0'), confidence: 85 };
    }

    // 6. Formato abreviado «Pol X - PYYYY» (cooperativas, huertos solares, naves)
    //    Ej: "Pol 14 - P104", "Pol 8 - P225", "Pol 12 - P15"
    //    Separador obligatorio guión; P pegada al número sin espacio intermedio.
    const polPShort = norm.match(/POL[.\s]*(?:GONO)?\s*(\d{1,3})\s*[-\u2013]\s*P(\d{1,5})/);
    if (polPShort) {
      return { patternType: 'polygon_parcel', polygon: polPShort[1].padStart(3, '0'), parcel: polPShort[2].padStart(5, '0'), confidence: 85 };
    }

    return null;
  }

  // ---------------------------------------------------------------------------
  // BÚSQUEDA POR DIRECCIÓN
  // ---------------------------------------------------------------------------

  async searchByAddress(codINE: string, via: string, numero?: string): Promise<CatastroR2Result[]> {
    await this.loadMunicipality(codINE);
    const cached = this.cache.get(codINE);
    if (!cached) return [];

    const viaNorm = this.normalizeVia(via);
    // CFU1 (09-ago-2026): una vía vacía no casa con nada — casaba con TODO.
    //
    // `''.includes('')` es `true`, de modo que la segunda condición de abajo
    // (`viaNorm.includes(pVia.substring(0, 0))`) se cumplía para **toda parcela
    // sin vía**: rústicas y suelo sin edificar, que no la tienen. Como el
    // `.slice(0, 10)` de más abajo respeta el orden del volcado, la MISMA parcela
    // salía una y otra vez, con confianza 70-85 —muy por encima del umbral 55— y
    // a kilómetros del elemento.
    //
    // Medido sobre el corpus de 5 municipios (`AUDIT_CFU1` §2): **114 de las 126
    // parcelas que `CTRN` adjuntaba eran una sola parcela-comodín por municipio**
    // —Alfacar 51, Colomera 26, Mancha Real 22, Cuevas Bajas 15—, todas con vía
    // vacía, `numero='S/N'`, `uso_cod` `Z` o `M`, a p50 2 225 m y hasta 19 128 m.
    //
    // La guarda es la causa medida y nada más. El umbral mínimo de longitud del
    // prefijo —hoy basta con 1 carácter, y `viaNorm.includes('LA')` casa de sobra—
    // es una segunda decisión SIN medir: queda como
    // `OBS-GEO-CATASTRO-PREFIJO-VIA-DEMASIADO-CORTO`, no se improvisa aquí.
    if (!viaNorm) return [];
    let matches = cached.dataset.parcels.filter(p => {
      const pVia = this.normalizeVia(`${p.via} ${p.nombre_via}`);
      if (!pVia) return false;
      return pVia.includes(viaNorm) || viaNorm.includes(pVia.substring(0, Math.min(10, pVia.length)));
    });

    if (numero && matches.length > 0) {
      const numNorm = numero.replace(/\D/g, '');
      const exact = matches.filter(p => p.numero.replace(/\D/g, '') === numNorm);
      // CATNUM-FIX (23-sep-2026, DOP-1 = A1): si se pidió un número y ninguna
      // parcela de la calle lo tiene, abstenerse. Antes se devolvían las 10
      // primeras de la calle en el orden del volcado, con conf 70 —por encima
      // del umbral 55 de L5 y de la adjunta—, es decir, la parcela de OTRO número.
      //
      // Medido por la ruta viva (`.ptel/p0_catnum_20260923/AUDIT_CATNUM.md`,
      // 219 filas de 4 municipios): se van 78 parcelas adjuntas equivocadas
      // (7 de ellas de otra calle), 4 filas dejan de moverse 42-144 m a la
      // parcela de otro número, y no se pierde ninguna parcela buena.
      //
      // Solo con número con dígitos: sin número, o con «s/n», el comportamiento
      // no cambia (se eligió A1, no A2). El umbral de prefijo de vía es otra
      // decisión: `OBS-GEO-CATASTRO-PREFIJO-VIA-DEMASIADO-CORTO`.
      if (numNorm && exact.length === 0) return [];
      if (exact.length > 0) matches = exact;
    }

    return matches
      .filter(m => this.isValidCoord(m))
      .slice(0, 10)
      .map(m => this.toResult(m, numero && m.numero.replace(/\D/g, '') === numero?.replace(/\D/g, '') ? 85 : 70, numero ? 15 : 30));
  }

  // ---------------------------------------------------------------------------
  // BÚSQUEDA FUZZY POR NOMBRE
  // ---------------------------------------------------------------------------

  async searchByName(codINE: string, name: string, usos?: string[]): Promise<CatastroR2Result[]> {
    await this.loadMunicipality(codINE);
    const cached = this.cache.get(codINE);
    if (!cached) return [];

    let candidates = cached.dataset.parcels;
    if (usos?.length) candidates = candidates.filter(p => usos.includes(p.uso_cod));
    if (candidates.length === 0) return [];

    const haystack = candidates.map(p => `${p.via} ${p.nombre_via} ${p.destino || ''} ${p.uso || ''}`.trim());
    const fuzzy = new uFuzzy(UFUZZY_OPTIONS);
    const [idxs, , order] = fuzzy.search(haystack, name);
    if (!idxs || idxs.length === 0) return [];

    const results: CatastroR2Result[] = [];
    const processOrder = order || idxs;
    for (let i = 0; i < Math.min(processOrder.length, 10); i++) {
      const p = candidates[idxs[processOrder[i]]];
      if (!this.isValidCoord(p)) continue;
      const score = 1 - i * 0.1;
      results.push({ ...this.toResult(p, this.fuzzyScoreToConfidence(score), 25), fuzzyScore: score });
    }
    return results;
  }

  // ---------------------------------------------------------------------------
  // BÚSQUEDA ESPACIAL POR PROXIMIDAD
  // ---------------------------------------------------------------------------

  async searchByProximity(codINE: string, x: number, y: number, options: CatastroSpatialOptions = {}): Promise<CatastroR2Result[]> {
    const { maxDistance = 100, limit = 10, usos } = options;
    await this.loadMunicipality(codINE);
    const cached = this.cache.get(codINE);
    if (!cached) return [];

    const { dataset, spatialIndex } = cached;
    const indices = spatialIndex.neighbors(x, y, limit * 2, maxDistance);
    const results: CatastroR2Result[] = [];

    for (const idx of indices) {
      if (results.length >= limit) break;
      const p = dataset.parcels[idx];
      if (usos?.length && !usos.includes(p.uso_cod)) continue;
      const dist = Math.sqrt((p.x - x) ** 2 + (p.y - y) ** 2);
      results.push({ ...this.toResult(p, dist < 25 ? 85 : dist < 50 ? 75 : 65, Math.round(dist)), fuzzyScore: undefined });
    }
    return results;
  }

  // ---------------------------------------------------------------------------
  // UTILIDADES
  // ---------------------------------------------------------------------------

  private isValidCoord(p: CatastroR2Parcel): boolean {
    return p.x >= X_RANGE_ANDALUCIA.min && p.x <= X_RANGE_ANDALUCIA.max
      && p.y >= Y_RANGE_ANDALUCIA.min && p.y <= Y_RANGE_ANDALUCIA.max;
  }

  private toResult(p: CatastroR2Parcel, confidence: number, precision: number): CatastroR2Result {
    return { x: p.x, y: p.y, confidence, precision, source: 'catastro_r2', parcel: p };
  }

  private normalizeVia(via: string): string {
    return via.toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/\b(CALLE|CL|AVENIDA|AV|AVDA|PLAZA|PL|PASEO|PS|PASAJE|PJ|CARRETERA|CR|CAMINO|CM|TRAVESIA|TV|RONDA|RD|GLORIETA|GL|URBANIZACION|URB|POLIGONO|PG)\b/g, '')
      .replace(/[^A-Z0-9]/g, '').trim();
  }

  private fuzzyScoreToConfidence(score: number): number {
    if (score >= 0.95) return 85;
    if (score >= 0.85) return 75;
    if (score >= 0.7) return 65;
    if (score >= 0.55) return 55;
    return 45;
  }

  /** Códigos de uso Catastro para una tipología PTEL */
  getUsosForTipologia(tipologia: string): string[] {
    const upper = tipologia.toUpperCase();
    // TUN3: el adaptador requiere TipologiaPTEL macro-19; validamos pertenencia.
    if ((TIPOLOGIAS_VALUES as readonly string[]).includes(upper)) {
      return tipologiaACodigoCatastro(upper as TipologiaPTEL);
    }
    return [];
  }

  getStats(): CatastroServiceStats {
    let totalParcels = 0;
    const loadedCodINEs: string[] = [];
    this.cache.forEach((data, codINE) => { loadedCodINEs.push(codINE); totalParcels += data.dataset.count; });
    return { municipiosLoaded: this.cache.size, totalParcels, loadedCodINEs };
  }

  clearCache(): void { this.cache.clear(); this.accessOrder = []; }

  // ---------------------------------------------------------------------------
  // CACHÉ LRU
  // ---------------------------------------------------------------------------

  private updateAccessOrder(codINE: string): void {
    const idx = this.accessOrder.indexOf(codINE);
    if (idx > -1) this.accessOrder.splice(idx, 1);
    this.accessOrder.push(codINE);
  }

  private addToCache(codINE: string, data: LoadedMunicipality): void {
    while (this.cache.size >= MAX_CACHED_MUNICIPALITIES) {
      const oldest = this.accessOrder.shift();
      if (oldest) { this.cache.delete(oldest); console.log(`[CatastroDataService] Evictado: ${oldest}`); }
    }
    this.cache.set(codINE, data);
    this.accessOrder.push(codINE);
  }
}

// =============================================================================
// SINGLETON
// =============================================================================

let instance: CatastroDataService | null = null;

export function getCatastroDataService(): CatastroDataService {
  if (!instance) instance = new CatastroDataService();
  return instance;
}

export function resetCatastroDataService(): void {
  if (instance) instance.clearCache();
  instance = null;
}
